import { useEffect } from "react";

/**
 * Lightweight global keyboard-shortcut hook.
 *
 * @param {Array<{
 *   keys: string|string[],      // 'ArrowLeft' or ['t','T']
 *   meta?: boolean,             // require ⌘ or Ctrl
 *   shift?: boolean,
 *   handler: (event: KeyboardEvent) => void,
 *   when?: () => boolean,       // optional gate
 * }>} shortcuts
 * @param {{ enabled?: boolean }} [opts]
 *
 * Behavior:
 *  • Ignores key events whose target is a form field (input/textarea/select/
 *    contenteditable) UNLESS `meta` is true (so ⌘+Enter to save works in the
 *    remark textarea).
 *  • Calls `event.preventDefault()` when a handler matches so the browser
 *    doesn't also act on the key.
 */
export function useKeyboardShortcuts(shortcuts, opts = {}) {
    const { enabled = true } = opts;
    useEffect(() => {
        if (!enabled) return undefined;
        const onKey = (e) => {
            const tag = (e.target?.tagName || "").toLowerCase();
            const isField =
                tag === "input" ||
                tag === "textarea" ||
                tag === "select" ||
                e.target?.isContentEditable;
            for (const s of shortcuts) {
                const keys = Array.isArray(s.keys) ? s.keys : [s.keys];
                if (!keys.includes(e.key)) continue;
                const wantMeta = !!s.meta;
                const hasMeta = e.metaKey || e.ctrlKey;
                if (wantMeta !== hasMeta) continue;
                if (!!s.shift !== e.shiftKey) continue;
                // Plain keys are suppressed in form fields. Meta-combo keys
                // always pass through.
                if (!wantMeta && isField) continue;
                if (s.when && !s.when()) continue;
                e.preventDefault();
                s.handler(e);
                return;
            }
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [shortcuts, enabled]);
}
